<template>
    <b-row>
        <b-col md="12">
            <component-header
                title="Role"
                :isLoading="loading"
                :hasSearch="true"
                :hasReload="true"
                :hasCreate="false"
                @search-query="(val) => (filter = val)"
                @reload-data="refreshRoles"
            />
        </b-col>
        <b-col md="12">
            <b-card class="card-wrap" border-variant="info">
                <create-role @newRole="insertRole" />
                <div class="table-wrapper">
                    <b-table
                        class="table-border-radius role-table mt-3"
                        responsive="sm"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        :dark="dark"
                        :hover="hover"
                        :striped="striped"
                        :bordered="bordered"
                        :small="small"
                        :fixed="fixed"
                        :fields="roleColumns"
                        :items="filteredList"
                        :current-page="currentPage"
                        :per-page="perPage"
                        thead-class="header"
                        :busy="loading"
                        show-empty
                    >
                        <template slot="cell(name)" slot-scope="data">
                            <div class="table-row">
                                {{ data.item.name }}
                            </div>
                        </template>
                        <template slot="cell(action)" slot-scope="data">
                            <div class="inline_btnTable">
                                <ActionButtonGroup
                                    showView
                                    showEdit
                                    showDelete
                                    @view="expandRole(data)"
                                    @edit="editRole(data)"
                                    @delete="deleteRole(data)"
                                />
                            </div>
                        </template>
                        <template slot="cell(row-details)" slot-scope="row">
                            <b-card class="role-details">
                                <div>
                                    <multiselect
                                        :id="row.item._id"
                                        :value="permissionMap[row.item._id]"
                                        :options="allPermissions"
                                        label="permission"
                                        track-by="permission"
                                        :multiple="true"
                                        @select="
                                            (selectedOption, id) =>
                                                addPermissionToRole(
                                                    selectedOption,
                                                    id,
                                                    row,
                                                )
                                        "
                                    ></multiselect>
                                </div>
                            </b-card>
                        </template>
                    </b-table>
                </div>
                <div class="pagination-wrapper">
                    <b-pagination
                        v-model="currentPage"
                        :per-page="perPage"
                        :total-rows="filteredList.length"
                        aria-controls="my-table"
                        prev-text="Prev"
                        next-text="Next"
                        hide-goto-end-buttons
                    ></b-pagination>
                </div>
            </b-card>
        </b-col>
        <b-modal v-model="roleModal" id="roleModal" centered>
            <template #modal-header>
                <div class="w-100 text-center modal-header-custom">
                    {{ modalHeader }}
                </div>
            </template>
            <div class="modal-body-custom">
                <b-form-group label="Role Name" label-for="role-name">
                    <b-input
                        id="role-name"
                        v-model="role.name"
                        placeholder="Enter role name"
                        class="modal-input"
                    />
                </b-form-group>
                <b-form-group label="Description" label-for="role-description">
                    <b-input
                        id="role-description"
                        class="mt-2 modal-input"
                        v-model="role.description"
                        placeholder="Enter role description"
                    />
                </b-form-group>
            </div>
            <template #modal-footer>
                <div class="modal-footer-custom">
                    <b-button
                        @click="clearModal"
                        class="modal-btn modal-btn-cancel"
                        variant="danger"
                        >Cancel</b-button
                    >
                    <b-button
                        @click="handleEdit"
                        :disabled="!role.name || !role.description"
                        variant="primary"
                        class="modal-btn modal-btn-update"
                        >Update</b-button
                    >
                </div>
            </template>
        </b-modal>
    </b-row>
</template>

<script>
import CreateRole from "@/components/Auth/CreateRole.vue";
import ComponentHeader from "@/components/componentNewHeader";
import { authMixin } from "@/auth/mixins";
import ActionButtonGroup from "@/components/Common/ActionButtonGroup.vue";

export default {
    name: "Role",
    components: {
        CreateRole,
        ComponentHeader,
        ActionButtonGroup,
    },
    mixins: [authMixin],
    props: {
        auth: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            modalHeader: "Create a Role",
            roles: [],
            filteredList: [],
            role: {},
            roleModal: false,
            roleColumns: [
                { key: "name", sortable: true },
                { key: "description" },
                { key: "action", class: "text-center" },
            ],
            loading: true,
            sortBy: "createdAt",
            sortDesc: true,
            perPage: 10,
            hover: true,
            striped: false,
            filter: "",
            bordered: true,
            small: false,
            fixed: false,
            dark: false,
            currentPage: 1,
            permissionMap: {},
            allPermissions: [],
        };
    },
    watch: {
        filter(newVal) {
            const list = this.$store.getters["role/GetRoleList"] || [];

            if (!newVal) {
                this.filteredList = list;
                return;
            }

            const search = newVal.toLowerCase();
            this.filteredList = list.filter((el) =>
                (el.name + " " + el.description).toLowerCase().includes(search),
            );
        },
    },
    beforeMount() {
        this.loading = true;
    },
    mounted() {
        this.loadRoles();
    },
    methods: {
        async loadRoles() {
            try {
                this.loading = true;
                await this.$store
                    .dispatch("role/fetchRoles")
                    .catch((error) => console.log(error));
                await this.$store
                    .dispatch("permission/fetchAllPermission")
                    .catch((error) => console.log(error));
                this.roles = this.$store.getters["role/GetRoleList"] || [];
                this.filteredList = [...this.roles];
                this.allPermissions =
                    this.$store.getters["permission/GetAllPermissionList"];
            } finally {
                this.loading = false;
            }
        },
        insertRole(payload) {
            this.$store
                .dispatch("role/createRole", payload)
                .then((result) => {
                    this.roles.push(result.response);
                    this.filteredList.push(result.response);
                    this.$swal({
                        title: "Success",
                        text: "Role created successfully!!",
                        type: "success",
                    });
                })
                .catch(() => this.$toasted.error("Name already exist"));
        },
        handleEdit() {
            const { _id, name, description } = this.role;
            const payload = {
                _id,
                data: { name, description },
            };
            this.$store
                .dispatch("role/updateRole", payload)
                .then(() => {
                    const index = this.roles.findIndex((r) => r._id === _id);
                    if (index !== -1) {
                        this.$set(
                            this.roles,
                            index,
                            JSON.parse(JSON.stringify(this.role)),
                        );
                    }
                    const index2 = this.filteredList.findIndex(
                        (item) => item._id === _id,
                    );
                    if (index2 !== -1) {
                        this.$set(
                            this.filteredList,
                            index2,
                            JSON.parse(JSON.stringify(this.role)),
                        );
                    }
                    this.$swal({
                        title: "Success",
                        text: "Role edited successfully!!",
                        type: "success",
                    });
                    this.clearModal();
                })
                .catch(() => this.clearModal());
        },
        deleteRole({ item }) {
            this.$swal({
                title: "Are you sure?",
                text: "You can't revert this action!!",
                type: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete it!",
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                reverseButtons: true,
            }).then((result) => {
                if (result.value) {
                    this.$store
                        .dispatch("role/deleteRole", item._id)
                        .then(() => {
                            this.roles = this.roles.filter(
                                (r) => r._id !== item._id,
                            );
                            this.filteredList = this.filteredList.filter(
                                (r) => r._id !== item._id,
                            );
                            this.$toasted.success("Deleted!");
                        });
                }
            });
        },
        clearModal() {
            this.role = {};
            this.roleModal = false;
        },
        editRole(row) {
            this.role = JSON.parse(JSON.stringify(row.item));
            this.modalHeader = "Edit a Role";
            this.roleModal = true;
        },
        refreshRoles() {
            this.loading = true;
            this.$store
                .dispatch("role/fetchRoles")
                .then(() => {
                    this.roles = this.$store.getters["role/GetRoleList"] || [];
                    this.filteredList = [...this.roles];
                })
                .catch((error) => console.log(error))
                .finally(() => {
                    this.loading = false;
                });
        },
        expandRole(row) {
            this.$router.push({
                name: "RolePermission",
                query: { role: row.item._id },
            });
        },
        addPermissionToRole(selectedOption, id, row) {
            const role = row.item._id;
            Object.assign(this.permissionMap, {
                [role]: this.permissionMap[role].concat([selectedOption]),
            });
        },
    },
};
</script>
<style scoped>
.edit {
    color: #ffffff !important;
    text-align: center !important;
}

.bin {
    color: white !important;
}

.row {
    display: flex;
    flex-wrap: wrap;
    margin-right: -15px;
    margin-left: -15px;
    margin-top: 10px;
}

.card-wrap {
    border-radius: 16px;
}

.table-wrapper {
    overflow-x: auto;
    width: 100%;
}

.role-table {
    min-width: 100%;
}

.circularIcon {
    height: 36px;
    width: 36px;
    border-radius: 50%;
    padding: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
}

.inline_btnTable .button-circle,
.inline_btnTable .circularIcon {
    width: 34px;
    height: 34px;
    min-width: 34px;
    min-height: 34px;
    border-radius: 50% !important;
    padding: 0 !important;
    margin: 0 !important;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
}

.inline_btnTable .button-circle i,
.inline_btnTable .circularIcon i {
    margin: 0;
    font-size: 14px;
}

.inline_btnTable {
    gap: 6px;
}

.card-header-custom {
    font-weight: 700;
    font-size: medium;
}

.table-border-radius {
    border-radius: 12px;
}

.btn-orange {
    color: #ffffff !important;
    background: #fe7b08 !important;
    border-color: #fe7b08 !important;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease;
}

.btn-orange:hover,
.btn-orange:focus,
.btn-orange:not(:disabled):not(.disabled):active,
.show > .btn-orange.dropdown-toggle {
    color: #ffffff !important;
    background: #e26c04 !important;
    border-color: #e26c04 !important;
    box-shadow: none !important;
}

.btn-orange.focus,
.btn-orange:focus {
    outline: 0;
}

.pagination-wrapper {
    float: right;
    margin-top: 1rem;
}

.modal-header-custom {
    font-weight: 600;
    font-size: 1.1rem;
}

.modal-body-custom {
    padding: 1rem 0;
}

.modal-input {
    width: 100%;
}

.modal-footer-custom {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    align-items: center;
    width: 100%;
}

.modal-btn {
    min-width: 130px;
    width: auto;
    height: 42px;
    min-height: 42px;
    padding: 0.5rem 1.25rem;
    font-size: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 !important;
}

.modal-btn-cancel,
.modal-btn-update {
    flex: 0 0 auto;
}

.role-details {
    margin: 10px;
}

.role-details .multiselect {
    width: 100%;
}

@media (max-width: 767.98px) {
    .pagination-wrapper {
        float: none;
        width: 100%;
        display: flex;
        justify-content: center;
        margin-top: 1rem;
    }

    .table-wrapper {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .circularIcon,
    .inline_btnTable .button-circle,
    .inline_btnTable .circularIcon {
        height: 32px;
        width: 32px;
        min-width: 32px;
        min-height: 32px;
    }

    .card-header-custom {
        font-size: 0.95rem;
        padding: 0.75rem 1rem;
    }

    .inline_btnTable {
        display: flex;
        gap: 6px;
        justify-content: center;
        flex-wrap: wrap;
    }

    .modal-footer-custom {
        flex-direction: row;
        gap: 0.75rem;
        width: 100%;
    }

    .modal-btn {
        flex: 1 1 0;
        min-width: 100px;
        max-width: 160px;
        width: auto;
        margin: 0 !important;
    }

    .modal-body-custom {
        padding: 0.5rem 0;
    }
}

@media (max-width: 576px) {
    .card-header-custom {
        font-size: 0.9rem;
        padding: 0.5rem 0.75rem;
    }

    .table-border-radius {
        font-size: 0.875rem;
    }

    .pagination-wrapper {
        margin-top: 0.75rem;
    }

    .pagination-wrapper ::v-deep .pagination {
        flex-wrap: wrap;
        justify-content: center;
    }

    .pagination-wrapper ::v-deep .page-link {
        padding: 0.375rem 0.5rem;
        font-size: 0.875rem;
    }
}
.inline_btnTable {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    gap: 6px;
}

/* Ensure all buttons in action column are circular on mobile */
@media (max-width: 767.98px) {
    .inline_btnTable .btn {
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        padding: 0 !important;
        border-radius: 50% !important;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
}

@media (max-width: 576px) {
    .inline_btnTable {
        flex-wrap: nowrap;
        gap: 4px;
    }

    .inline_btnTable .btn {
        margin-top: 0 !important;
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        border-radius: 50% !important;
    }
}
</style>
<!-- Edit Role modal: non-scoped so styles apply when modal is portaled to body -->
<style>
.modal-footer-custom {
    display: flex !important;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    align-items: center;
    width: 100%;
}

.modal-footer-custom .modal-btn {
    min-width: 130px;
    width: auto;
    height: 42px;
    min-height: 42px;
    padding: 0.5rem 1.25rem;
    font-size: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 !important;
}

@media (max-width: 767.98px) {
    .modal-footer-custom {
        flex-direction: row !important;
        gap: 0.75rem;
    }

    .modal-footer-custom .modal-btn {
        flex: 1 1 0 !important;
        min-width: 100px !important;
        max-width: 160px !important;
        width: auto !important;
        margin: 0 !important;
    }
}
</style>
